<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="600px"
  >
    <template v-slot:activator="{ on }">
      <v-btn 
        icon 
        v-on="on"
      >
        <v-icon color="primary">
          mdi-pencil
        </v-icon>
      </v-btn>
    </template>

    <material-card
      :offset="10"
      :full-width="true"
      color="green"
      title="Exam"
      text="Edit Exam"
      class="mb-0"
    >
      <v-container v-if="dialog">
        <v-row no-gutters>
          <v-col cols="12">
            <v-text-field
              v-model="localExam.name"
              v-validate="'required|max:255'"
              :error-messages="errors.collect('name')"
              data-vv-name="name"        
              label="Name"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="localExam.passing_score"
              v-validate="'required|max:100|min:0'"
              :error-messages="errors.collect('passing score')"
              data-vv-name="passing score"        
              type="number"
              label="Passing Score"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="localExam.duration"
              v-validate="'required|min:1'"
              :error-messages="errors.collect('duration')"
              data-vv-name="duration"        
              type="number"
              label="Duration (minutes)"
            />
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="localExam.max_attempt"
              v-validate="'required|min:1'"
              :error-messages="errors.collect('max_attempt')"
              data-vv-name="max attempt"        
              type="number"
              label="Max Attempt"
            />
          </v-col>

          <v-col cols="6">
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="true"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="localStartDate"
                  v-validate="'required'"
                  label="Exam Start Date"
                  prepend-icon="event"
                  :error-messages="errors.collect('start_date')"
                  data-vv-name="start_date"  
                  data-vv-as="start date"      
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="localStartDate"
              />
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-menu
              v-model="startTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="starTimeDisplay"
                  :rules="rules"
                  label="Exam Start Time"
                  prepend-icon="mdi-clock"
                  readonly
                  v-on="on"
                />
              </template>

              <v-time-picker
                ref="picker"
                v-model="localStartTime"
                :landscape="true"
                width="190"
                scrollable
                @click:minute="startTimeMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="6">
            <v-menu
              v-model="endDateMenu"
              :close-on-content-click="true"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="localEndDate"
                  v-validate="'required'"
                  label="Exam End Date"
                  prepend-icon="event"
                  :error-messages="errors.collect('end_date')"
                  data-vv-name="end_date"  
                  data-vv-as="end date"
                  readonly
                  v-on="on"
                />
              </template>
              <v-date-picker
                v-model="localEndDate"
              />
            </v-menu>
          </v-col> 
          <v-col cols="6">
            <v-menu
              v-model="endTimeMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="endTimeDisplay"
                  :rules="rules"
                  label="Exam End Time"
                  prepend-icon="mdi-clock"
                  readonly
                  v-on="on"
                />
              </template>
              <v-time-picker
                ref="picker"
                v-model="localEndTime"
                :landscape="true"
                width="190"
                scrollable
                @click:minute="endTimeMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-container>

      <v-card-actions>
        <v-spacer />
        <v-btn
          :disabled="progressing"
          text
          @click="dialog = false"
        >
          Close
        </v-btn>
        <v-btn
          :loading="progressing"
          color="success"          
          @click="submitForm"
        >
          Update
        </v-btn>
      </v-card-actions>
    </material-card>
  </v-dialog>
</template>

<script>
  export default {
    inject: ['$validator'],
    props: {
      exam: {
        type: Object,
        required: true,
      },
    },
    data () {
      return {
        dialog: false,
        progressing: false,
        localExam: null,
        startDateMenu: false,
        localStartDate: null,
        startTimeMenu: false,
        localStartTime: null,
        
        endDateMenu: false,
        localEndDate: null,
        endTimeMenu: false,
        localEndTime: null,

        rules: [
          value => !!value || 'Required.',
        ],
      }
    },
    computed: {
      starTimeDisplay () {
        return this.localStartTime ? this.formatTime(this.localStartTime) : ''
      },
      endTimeDisplay () {
        return this.localEndTime ? this.formatTime(this.localEndTime) : ''
      },
    },

    watch: {
      dialog: function (value) {
        if (!value) return

        this.localExam = { ...this.exam }
        this.localStartDate = this.localExam.start_date.substring(0, 10)
        this.localStartTime = this.localExam.start_date.substring(11, this.localExam.start_date.length - 3)
        this.localEndDate = this.localExam.end_date.substring(0, 10)
        this.localEndTime = this.localExam.end_date.substring(11, this.localExam.end_date.length - 3)
      },
    },

    methods: {
      submitForm () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return
            
            this.progressing = true

            this.$api.exams().update(this.exam.id, this.prepareFormData())
              .then(res => {
                this.$emit('updated')
                this.$snack({ message: 'Exam updated successfully' })
                this.dialog = false
              })
              .catch(() => {
                this.$snack({ 
                  color: 'error',
                  message: 'Failed to update exam', 
                })
              })
              .then(() => {
                this.progressing = false
              })
          })
      },

      formatTime (time) {
        let a = time.split(':') // split it at the colons
        let seconds = (a[0] * 60 * 60) + (a[1] * 60)

        return this.$moment(new Date(1970, 0, 1).setSeconds(seconds)).format('hh:mm A')
      },

      prepareFormData () {
        return {
          'name': this.localExam.name,
          'passing_score': this.localExam.passing_score,
          'duration': this.localExam.duration,
          'max_attempt': this.localExam.max_attempt,
          'start_date': this.localStartDate + ' ' + this.localStartTime + ':00',
          'end_date': this.localEndDate + ' ' + this.localEndTime + ':00',
        }
      },
    },
  }
</script>
